import { HandledError } from "./index"
import {
  BaseResponse,
  SingleResponse,
  ErrorContainer,
  InternalServerError,
  isTooManyRequestsError,
} from "@tastyworks/tastyworks-api"
import { alertDisplay } from "./text"

//////////////////////////////////
// Types
//////////////////////////////////

// Represent backend tables; can be added to
type RestResource =
  | "Account"
  | "AccountDocument"
  | "AccountOpeningApplication"
  | "AuthenticatorConfirmation"
  | "AvailableBuyingPower"
  | "CommunicationChannelConsent"
  | "CommunicationGroupPreference"
  | "Customer"
  | "CustomerSuitability"
  | "DocumentRecord" // Uploaded document (e.g., driver's license)
  | "Document" // Documents to view (e.g., agreements with separate URL)
  | "Enablement"
  | "Entity"
  | "EntitySuitability"
  | "ExternalTransaction"
  | "FuturesAgreementRecord"
  | "JournalEntry"
  | "KYC"
  | "MobileConfirmation"
  | "OptionsLevel"
  | "PermittedAccountType"
  | "PlaidLayerIdentity"
  | "PlaidLayerLinkToken"
  | "Referral"
  | "RequiredDocument" // Documents required for the user to upload
  | "SecurityQuestion"
  | "Session"
  | "SupportingDocument" // Documents requested for the user to upload
  | "TrustedContact"
  | "User"

type RestOperation =
  | "create"
  | "read"
  | "update"
  | "submit"
  | "delete"
  | "validate"

// Represent `errorContainer.code` sent from the backend; can be added to
type RestErrorCode =
  | "internal_server_error"
  | "record_not_found"
  | "validation_error"

type DuplicateLoginCode =
  | "username_taken"
  | "email_taken"
  | "email_and_username_taken"

// Failure types used to group error codes in Sentry, so we should NOT change unless there is a compelling Sentry reason
type RestFailure =
  | "Internal error"
  | "Record not found"
  | "Validation error"
  | "Rate limited"
  | "Empty response" // Missing both `data` and `error`
  | "Unknown error" // Unexpected or missing `code`

export interface RestContext {
  resource: RestResource
  operation: RestOperation
}

interface RestDetail {
  errorContainer?: ErrorContainer
  errorDetails?: string[]
  method?: string
  url?: string
  cause?: unknown
}

//////////////////////////////////
// Class Implementation
//////////////////////////////////

export class RestError extends HandledError {
  private constructor(
    readonly failure: RestFailure,
    readonly context: RestContext,
    readonly detail: RestDetail
  ) {
    super("RestError", describe(context, failure))
  }

  static fromResponse(response: BaseResponse, context: RestContext) {
    const failure = classify(response)
    const errorContainer = response.errorContainer
    // Sentry truncates past three levels, so flatten the details it would drop
    const errorDetails = errorContainer.errors.map((error) => error.toString())

    return new RestError(failure, context, { errorContainer, errorDetails })
  }

  static fromThrown(e: unknown, context: RestContext) {
    // TODO: Error handling for AbortError
    if (e instanceof InternalServerError) {
      return new RestError("Internal error", context, {
        method: e.method,
        url: e.url,
      })
    } else if (isTooManyRequestsError(e)) {
      return new RestError("Rate limited", context, {
        method: e.method,
        url: e.url,
      })
    }

    return new RestError("Unknown error", context, { cause: e })
  }

  override get userFriendlyDisplay() {
    const code = this.detail.errorContainer?.code

    if (code && isDuplicateLoginCode(code)) {
      return alertDisplay.restError.byErrorCode[code]
    }

    switch (this.failure) {
      case "Internal error":
      case "Rate limited":
        return alertDisplay.restError.byFailure[this.failure]
    }

    return alertDisplay.generic
  }

  override get fingerprint() {
    const { resource, operation } = this.context
    const { errorContainer, errorDetails, cause } = this.detail
    const result: string[] = [resource, operation, this.failure]

    if (errorContainer?.code) {
      result.push(errorContainer.code)
    }
    if (errorDetails?.length) {
      // Backend order isn't guaranteed; Sentry fingerprints are order-sensitive
      result.push(...[...errorDetails].sort())
    }
    if (cause instanceof Error) {
      result.push(cause.name)
    }

    return result
  }

  override isSentryIgnored() {
    if (this.isRateLimited) {
      return true
    }

    if (!this.detail.errorContainer) return false
    return isDuplicateLoginCode(this.detail.errorContainer.code)
  }

  get isRateLimited() {
    return this.failure === "Rate limited"
  }

  get isRecordNotFound() {
    return this.detail.errorContainer?.code === "record_not_found"
  }
}

//////////////////////////////////
// Helpers
//////////////////////////////////

const DUPLICATE_LOGIN_CODES: DuplicateLoginCode[] = [
  "username_taken",
  "email_taken",
  "email_and_username_taken",
]

function isDuplicateLoginCode(code: string): code is DuplicateLoginCode {
  return DUPLICATE_LOGIN_CODES.includes(code as DuplicateLoginCode)
}

const CODE_TO_FAILURE_MAP: Record<RestErrorCode, RestFailure> = {
  internal_server_error: "Internal error",
  record_not_found: "Record not found",
  validation_error: "Validation error",
}

function classify(response: BaseResponse): RestFailure {
  if (response instanceof SingleResponse) {
    if (response.isError) {
      return deriveFailureFromErrorCode(response.errorContainer.code)
    }
    if (!response.hasData()) return "Empty response"
  }

  // ItemsResponse will always have `items` (which is valid empty) if `isError === false`
  if (response.isError) {
    return deriveFailureFromErrorCode(response.errorContainer.code)
  }

  return "Unknown error"
}

function deriveFailureFromErrorCode(code: string): RestFailure {
  if (!(code in CODE_TO_FAILURE_MAP)) return "Unknown error"
  return CODE_TO_FAILURE_MAP[code as RestErrorCode]
}

function describe({ resource, operation }: RestContext, failure: RestFailure) {
  return `${resource} ${operation} failed with: ${failure}`
}
